import { useState } from 'react';
import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';

import { api } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { requirePermission } from '@/lib/route-guards';
import { VEHICLE_STATUS_LABELS, VEHICLE_STATUS_VARIANT, fuelLabel } from '@/lib/vehicles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';

import type { Paginated, Vehicle } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/fleet')({
  beforeLoad: () => requirePermission('vehicles.read'),
  component: FleetPage,
});

function FleetPage() {
  const matchRoute = useMatchRoute();
  const isSubRoute = !!(matchRoute({ to: '/fleet/new' }) || matchRoute({ to: '/fleet/$id' }));
  if (isSubRoute) return <Outlet />;
  return <FleetList />;
}

function FleetList() {
  const [search, setSearch] = useState('');
  const canWrite = useHasPermission('vehicles.write');

  const query = useQuery({
    queryKey: ['vehicles', search],
    queryFn: async () =>
      (await api.get<Paginated<Vehicle>>(`/vehicles?search=${encodeURIComponent(search)}&pageSize=100`)).data,
  });
  const { data, isLoading } = query;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Flotte</h1>
        {canWrite && (
          <Button asChild>
            <Link to="/fleet/new">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un véhicule
            </Link>
          </Button>
        )}
      </div>
      <QueryError query={query} />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher par plaque, marque…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plaque</TableHead>
              <TableHead>Marque / Modèle</TableHead>
              <TableHead>Carburant</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Odo (km)</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {data?.data?.map((v) => (
              <TableRow key={v.id}>
                <TableCell className="font-mono font-semibold">{v.plate}</TableCell>
                <TableCell>{[v.make, v.model].filter(Boolean).join(' ')}</TableCell>
                <TableCell>{fuelLabel(v.fuelType)}</TableCell>
                <TableCell>
                  <Badge variant={VEHICLE_STATUS_VARIANT[v.status]}>{VEHICLE_STATUS_LABELS[v.status] ?? v.status}</Badge>
                </TableCell>
                <TableCell className="text-right">{v.odometerKm.toLocaleString('fr-FR')}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/fleet/$id" params={{ id: v.id }}>Détail</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data && (data.data?.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Aucun véhicule trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
